import React from 'react';

import { omit } from 'lodash';
import { Button } from '../../../../components/Button/Button';
import { Card } from '../../../../components/Card/Card';
import { KeyValueTable } from '../../../../components/KeyValueTable/KeyValueTable';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { StatusPill } from '../../../../components/StatusPill/StatusPill';
import { graphql } from '../../../../generated';
import { t } from '../../../../locales/translate';
import { useQueryHandler } from '../../../../model/graphql';
import { CustomEmailSenderForm } from './CustomEmailSenderForm';

const CUSTOM_EMAIL_SENDER = graphql(`
  query CustomEmailSender($id: ID!) {
    shop(id: $id) {
      id
      customEmailSender {
        id
        active
        displayName
        email

        headerImage {
          id
          url(width: 300)
          originalUrl
          alt
          fileType
          order
          tags
        }

        bodyBackgroundColor
        bodyForegroundColor
        buttonBackgroundColor
        buttonForegroundColor
        footerBackgroundColor
        footerForegroundColor
        emailBackgroundColor
        separatorLineColor
        closingNotes
      }
    }
  }
`);

interface Props {
  shopId: string;
}

export function CustomEmailSenderCard({ shopId }: Props): JSX.Element | null {
  const { data, loading } = useQueryHandler(CUSTOM_EMAIL_SENDER, {
    variables: { id: shopId },
  });

  const modal = useModalPresenter();

  if (!data?.shop) {
    return null;
  }

  return (
    <>
      <Card
        loading={loading}
        elevation={1}
        title={
          <>
            <p>{t.model.shop.customEmailSender}</p>
            <StatusPill status={data.shop?.customEmailSender?.active ? 'active' : 'inactive'} />
          </>
        }
        actions={
          <Button
            icon="edit"
            onClick={() =>
              modal.present(CustomEmailSenderForm, {
                customEmailSender: omit(data.shop?.customEmailSender, '__typename'),
                shopId,
              })
            }
          />
        }
      >
        {data.shop.customEmailSender?.active && (
          <>
            <KeyValueTable
              items={[
                {
                  key: t.model.shop.customEmailSenderFields.displayName,
                  value: data.shop.customEmailSender.displayName,
                },
                {
                  key: t.model.shop.customEmailSenderFields.email,
                  value: data.shop.customEmailSender.email,
                },
                data.shop.customEmailSender.headerImage?.__typename === 'Image' && {
                  key: t.model.shop.customEmailSenderFields.headerImage,
                  value: (
                    <>
                      <a
                        href={data.shop.customEmailSender.headerImage.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View Original Image"
                      >
                        <img src={data.shop.customEmailSender.headerImage.url} />
                      </a>
                    </>
                  ),
                },
                {
                  key: t.model.shop.customEmailSenderFields.bodyBackgroundColor,
                  value: data.shop.customEmailSender.bodyBackgroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.bodyForegroundColor,
                  value: data.shop.customEmailSender.bodyForegroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.buttonBackgroundColor,
                  value: data.shop.customEmailSender.buttonBackgroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.buttonForegroundColor,
                  value: data.shop.customEmailSender.buttonForegroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.footerBackgroundColor,
                  value: data.shop.customEmailSender.footerBackgroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.footerForegroundColor,
                  value: data.shop.customEmailSender.footerForegroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.emailBackgroundColor,
                  value: data.shop.customEmailSender.emailBackgroundColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.separatorLineColor,
                  value: data.shop.customEmailSender.separatorLineColor,
                },
                {
                  key: t.model.shop.customEmailSenderFields.closingNotes,
                  value: data.shop.customEmailSender.closingNotes,
                },
              ]}
            />
          </>
        )}
      </Card>
    </>
  );
}
